import { type z } from 'zod';

import {
	AppFrameProfilingEventSchema,
	ClickedProfilingEventSchema,
	FCPProfilingEventSchema,
	LayoutShiftProfilingEventSchema,
	LCPProfilingEventSchema,
	NavigationTimingProfilingEventSchema,
	ResourceTimingProfilingEventSchema,
	type FrameProfilingEventType,
} from './frameEventValidations';
import { getPerformanceMark, type AppPerformanceProfilingEvent } from '.';

const ClickedProfilingCapturableEventTransform =
	ClickedProfilingEventSchema.transform<AppPerformanceProfilingEvent>((_) => {
		return {
			type: 'user-interacted',
			elapsed: getPerformanceMark(),
		};
	});

const NavigationTimingProfilingCapturableEventTransform =
	NavigationTimingProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) => {
		return {
			type: 'navigation-timing',
			elapsed: getPerformanceMark(),
			duration: Math.trunc(e.duration),
			transferSize: e.transferSize,
		};
	});

const ResourceTimingProfilingCapturableEventTransform =
	ResourceTimingProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) => {
		return {
			type: 'resource-timing',
			elapsed: getPerformanceMark(),
			start: Math.trunc(e.start),
			duration: Math.trunc(e.duration),
			transferSize: Math.trunc(e.transferSize),
			initiatorType: e.initiatorType,
		};
	});

const LayoutShiftProfilingCapturableEventTransform =
	LayoutShiftProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) => {
		return {
			type: 'dcls-score',
			elapsed: getPerformanceMark(),
			timestamp: Math.trunc(e.timestamp),
			// Transport 2dp truncated to avoid precision errors.
			score: Math.trunc(e.score * 100),
			inputFlag: e.inputFlag,
		};
	});

const FCPProfilingCapturableEventTransform =
	FCPProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) => {
		return {
			type: 'fcp-snapshot',
			elapsed: getPerformanceMark(),
			start: Math.trunc(e.start),
		};
	});

const LCPProfilingCapturableEventTransform =
	LCPProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) => {
		return {
			type: 'lcp-snapshot',
			elapsed: getPerformanceMark(),
			start: Math.trunc(e.start),
			size: Math.trunc(e.size),
		};
	});

// Zod discriminated unions don't work with transforms on the narrowed types.
// Exhaustive-keyed map ensures future events added have transformers.
type ExhaustiveFrameEventTransformMap = {
	[K in FrameProfilingEventType['event']]: z.ZodEffects<
		z.ZodTypeAny,
		AppPerformanceProfilingEvent,
		FrameProfilingEventType
	>;
};

// For each in-frame event type, there should be a transformer to a safely ingestible event.
const transformers: ExhaustiveFrameEventTransformMap = {
	'navigation-timing': NavigationTimingProfilingCapturableEventTransform,
	'resource-timing': ResourceTimingProfilingCapturableEventTransform,
	'dcls-score': LayoutShiftProfilingCapturableEventTransform,
	click: ClickedProfilingCapturableEventTransform,
	'fcp-snapshot': FCPProfilingCapturableEventTransform,
	'lcp-snapshot': LCPProfilingCapturableEventTransform,
};

export const FrameProfilingEventCaptureTransformer =
	AppFrameProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) =>
		transformers[e.event].parse(e),
	);
